import { useState, useEffect, useCallback, useMemo } from 'react'
import { Table, Input } from 'antd'
import {
  CaretRightOutlined,
  CaretDownOutlined,
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@ant-design/icons'

import dayjs from 'dayjs'
import MovementsAvailabilitiesAdditionalMenu from './TableComponents/MovementsAvailabilitiesAdditionalMenu'
import CustomSort from '../Generic/CustomSort'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import { renderTranslation, calculateColumnWidthBasedOnText } from '../../Helpers/GenericHelpers'
import { setCapitalLetter } from '../../Helpers/StringHelpers'
import {
  Causal,
  Quantity,
  DateHeader,
  HAStoreName,
  EnableMovementLog,
  EditDate,
  EditDateColLength,
  HandlingExpandColLength,
  UserDetail,
  DocumentationNumber,
  ProgressiveAvailability,
  CausalColLength,
  QuantityColLength,
  DateHeaderColLength,
  ProgressiveAvailabilityColLength,
  DocumentationNumberColLength,
  MovementAvailabilitiesDefaultColLength,
  DetailExpandColLength,
  HAStoreNameAvailabilitiesLogColLength,
  DateFormat,
  TimeFormat,
  OperationId,
  OperationIdColLength,
  SizeFilterDefaultValue,
  MarketplaceDefaultValue,
  AvailTableColumnTranslationPrefix,
  CheckValueForAvailability,
} from '../../Constants/Configuration'
import { TableSortingDescend, TableSortingAscend } from '../../Constants/TableConfiguration'
import { setSortInfo } from '../../Slices/Kiosk/MovementsAvailabilitiesSlice'
import { retrieveArticleDetailMovementsAndAvailabilites } from '../../Services/Kiosk/ArticleServices'
import NumericCell from '../Generic/NumericCell'
import { retrieveSelectedArticleKey } from '../../Helpers/ArticlesHelper'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { TablePaginationConfig } from 'antd/es/table/interface'
import { customSortTableData, filterDataByCausalAndStore } from 'Helpers/FilterHelper'
import { mapSizeColumnData } from 'Helpers/TableHelper'

function Availabilities() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const dataSource = useAppSelector(state => state.present.articleDetail.articleSelected.availabilities)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const tableColumns = useAppSelector(state => state.present.movementsAvailabilities.value.availabilitiesColumns)
  const updateLogTableColumns = useAppSelector(
    state => state.present.movementsAvailabilities.value.availabilitiesUpdateLogTableColumns,
  )
  const availabilitiesFilter = useAppSelector(state => state.present.movementsAvailabilities.value.availabilitiesFilter)
  const sortInfo = useAppSelector(state => state.present.movementsAvailabilities.value.sortInfo)
  const currentCausal = useAppSelector(state => state.present.movementsAvailabilities.value.filterCausal)
  const sizeSelect = useAppSelector(state => state.present.movementsAvailabilities.value.size)
  const selectedMarketplace = useAppSelector(state => state.present.movementsAvailabilities.value.selectedMarketplace)

  const [isDetailsExpand, setIsDetailsExpand] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  useEffect(() => {
    setIsDetailsExpand(false)
  }, [articleKey])

  const toggleDetailsColumns = useCallback(() => {
    setIsDetailsExpand(!isDetailsExpand)
  }, [isDetailsExpand])

  const handleUpdateTableData = useCallback(() => {
    retrieveArticleDetailMovementsAndAvailabilites(articleKey, dispatch)
  }, [dispatch, articleKey])

  const setAscendSorting = useCallback(
    sortColumn => {
      dispatch(setSortInfo({ order: TableSortingAscend, columnKey: sortColumn }))
    },
    [dispatch],
  )

  const setDescendSorting = useCallback(
    sortColumn => {
      dispatch(setSortInfo({ order: TableSortingDescend, columnKey: sortColumn }))
    },
    [dispatch],
  )

  const resetSortOrder = useCallback(() => {
    dispatch(setSortInfo({}))
  }, [dispatch])

  const isEnableMovementLogActive = useMemo(() => {
    return availabilitiesFilter.filter(filter => filter.value === EnableMovementLog && filter.active).length !== 0
  }, [availabilitiesFilter])

  const renderedColumns = useMemo(() => {
    const renderedColumns = []
    if (tableColumns.length > 0) {
      tableColumns.forEach(element => {
        if (element.key === Causal) {
          renderedColumns.push({
            title: ({ sortOrder, sortColumn, filters }) => (
              <MovementsAvailabilitiesAdditionalMenu
                sortOrder={sortOrder}
                sortColumn={sortColumn}
                update={handleUpdateTableData}
              >
                <div>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                </div>
              </MovementsAvailabilitiesAdditionalMenu>
            ),
            dataIndex: element.key,
            key: element.key,
            width: CausalColLength,
            render: (text, record, index) => <div className=''>{text}</div>,
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            className: 'causal-column',
          })
        } else if (element.key === CheckValueForAvailability) {
          renderedColumns.push({
            title: (
              <div className='custom-sort-head'>
                <span>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter('quantity')],
                  )}
                </span>
                <CustomSort
                  sortInfo={sortInfo}
                  sortColumn={element.key}
                  setAscendSorting={setAscendSorting}
                  setDescendSorting={setDescendSorting}
                  resetSortOrder={resetSortOrder}
                />
              </div>
            ),
            dataIndex: element.key,
            key: element.key,
            width: QuantityColLength,
            render: (text, record) => {
              return <NumericCell value={text} showZeroValue />
            },
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            align: 'center',
            className: '',
          })
        } else if (element.key === DateHeader) {
          renderedColumns.push({
            title: (
              <div className='custom-sort-head'>
                <span>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                </span>
                <CustomSort
                  sortInfo={sortInfo}
                  sortColumn={element.key}
                  setAscendSorting={setAscendSorting}
                  setDescendSorting={setDescendSorting}
                  resetSortOrder={resetSortOrder}
                />
              </div>
            ),
            dataIndex: element.key,
            key: element.key,
            width: DateHeaderColLength,
            render: (text, record, index) => <div className=''>{text}</div>,
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            className: '',
          })
        } else if (element.key === ProgressiveAvailability) {
          renderedColumns.push({
            title: (
              <div className='custom-sort-head'>
                <span>
                  {renderTranslation(
                    language,
                    TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                </span>
                <CustomSort
                  sortInfo={sortInfo}
                  sortColumn={element.key}
                  setAscendSorting={setAscendSorting}
                  setDescendSorting={setDescendSorting}
                  resetSortOrder={resetSortOrder}
                />
              </div>
            ),
            dataIndex: element.key,
            key: element.key,
            width: ProgressiveAvailabilityColLength,
            render: (text, record) => {
              const SellOrBuyClass = record[CheckValueForAvailability]
                ? record[CheckValueForAvailability] < 0
                  ? 'sell'
                  : record[CheckValueForAvailability] > 0
                  ? 'purchase'
                  : ''
                : ''
              return <NumericCell value={text} extraClass={SellOrBuyClass} showLimitWarning={record.limitWarning} />
            },
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            align: 'center',
            className: '',
          })
        } else {
          renderedColumns.push({
            title:
              element.key === HAStoreName ? (
                <div className='custom-sort-head'>
                  <span className='availabilities-input-container'>
                    <Input
                      variant={'borderless'}
                      placeholder={renderTranslation(
                        language,
                        TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                      )}
                      addonBefore={<SearchOutlined className='input-search-icon' />}
                      className='availabilities-search'
                      value={searchValue}
                      onChange={e => {
                        setSearchValue(e.target.value)
                      }}
                      onClick={e => {
                        e.stopPropagation()
                      }}
                      allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
                    />
                  </span>
                  <CustomSort
                    sortInfo={sortInfo}
                    sortColumn={element.key}
                    setAscendSorting={setAscendSorting}
                    setDescendSorting={setDescendSorting}
                    resetSortOrder={resetSortOrder}
                  />
                </div>
              ) : (
                <div className='custom-sort-head'>
                  <span>
                    {renderTranslation(
                      language,
                      TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                    )}
                  </span>
                  <CustomSort
                    sortInfo={sortInfo}
                    sortColumn={element.key}
                    setAscendSorting={setAscendSorting}
                    setDescendSorting={setDescendSorting}
                    resetSortOrder={resetSortOrder}
                  />
                </div>
              ),
            dataIndex: element.key,
            key: element.key,
            width:
              element.key === DocumentationNumber
                ? DocumentationNumberColLength
                : MovementAvailabilitiesDefaultColLength,
            render: (text, record, index) => (
              <div className={element.key === HAStoreName ? 'store-label' : ''}>{text}</div>
            ),
            sortOrder: sortInfo.columnKey && sortInfo.columnKey === element.key ? sortInfo.order : null,
            className: '',
          })
        }
      })

      if (!isDetailsExpand) {
        renderedColumns.push({
          title: (
            <div onClick={toggleDetailsColumns} className='cursor-pointer'>
              <PlusOutlined /> {renderTranslation(language, TranslationKeys.tableAvailabilitiesDetails)}
            </div>
          ),
          dataIndex: 'detailColumn',
          key: 'detailColumn',
          width: DetailExpandColLength,
          align: 'center',
          className: 'column-no-border',
        })
      }

      if (isDetailsExpand) {
        const sizes = dataSource
          .reduce((prev, curr) => {
            return curr.sizes ? prev.concat(curr.sizes) : prev
          }, [])
          .sort((a, b) => a.name.localeCompare(b.name))
        sizes.forEach(size => {
          if (renderedColumns.filter(column => column.key === 'detail' + size.name).length === 0) {
            renderedColumns.push({
              title: size.name,
              dataIndex: 'detail' + size.name,
              key: 'detail' + size.name,
              width: calculateColumnWidthBasedOnText(size.name),
              align: 'center',
              className: 'detail-size ',
              render: text => <NumericCell value={text} highlightNegative />,
            })
          }
        })

        renderedColumns.push({
          title: (
            <div onClick={toggleDetailsColumns} className='cursor-pointer'>
              <MinusOutlined /> {renderTranslation(language, TranslationKeys.tableAvailabilitiesDetails)}
            </div>
          ),
          dataIndex: 'detailColumn',
          key: 'detailColumn',
          width: DetailExpandColLength,
          align: 'center',
          className: 'column-no-border',
        })
      }
    }
    return renderedColumns
  }, [
    dataSource,
    handleUpdateTableData,
    isDetailsExpand,
    language,
    resetSortOrder,
    searchValue,
    setAscendSorting,
    setDescendSorting,
    sortInfo,
    tableColumns,
    toggleDetailsColumns,
  ])

  const results = useMemo(() => {
    return dataSource &&
      dataSource.length > 0 &&
      sizeSelect !== SizeFilterDefaultValue &&
      selectedMarketplace !== MarketplaceDefaultValue
      ? dataSource
      : []
  }, [selectedMarketplace, sizeSelect, dataSource])

  const filterResults = useCallback(
    results => {
      return filterDataByCausalAndStore(results, currentCausal, searchValue)
    },
    [currentCausal, searchValue],
  )

  const sortedData = useMemo(() => {
    const resultsToDisplay = mapSizeColumnData(filterResults(results))

    return customSortTableData(resultsToDisplay, sortInfo)
  }, [filterResults, results, sortInfo])

  const getColumnWidth = useCallback(
    curr => {
      if (isDetailsExpand) {
        if (curr.width) {
          return curr.width
        }
      } else {
        return curr.width
          ? curr.width
          : curr.className === 'column-no-border'
          ? DetailExpandColLength
          : MovementAvailabilitiesDefaultColLength
      }
    },
    [isDetailsExpand],
  )

  const expandedLogRowRender = useCallback(
    (record, index, indent, expanded) => {
      const logColumns = []
      const logData = []

      if (record.logs && record.logs.length !== 0) {
        record.logs.forEach((log, index) => {
          const logToAdd = { ...log }
          logToAdd['rowId'] = record.teId + '-' + index
          logData.push(logToAdd)
        })
      }

      if (updateLogTableColumns.length > 0) {
        updateLogTableColumns.forEach(element => {
          if (element.key === Quantity) {
            logColumns.push({
              title: element.key,
              dataIndex: element.key,
              key: element.key,
              width: QuantityColLength,
              render: (text, record) => {
                const SellOrBuyClass = record[CheckValueForAvailability]
                  ? record[CheckValueForAvailability] < 0
                    ? 'sell'
                    : record[CheckValueForAvailability] > 0
                    ? 'purchase'
                    : ''
                  : ''
                return <NumericCell value={text} extraClass={SellOrBuyClass} />
              },
              className: 'extra-log-quantity-col ',
            })
          } else if (element.key === ProgressiveAvailability) {
            logColumns.push({
              title: element.key,
              dataIndex: element.key,
              key: element.key,
              width: ProgressiveAvailabilityColLength,
              render: (text, record) => {
                return <NumericCell value={text} showLimitWarning={record.limitWarning} />
              },
              align: 'center',
              className: '',
            })
          } else {
            if (element.key !== UserDetail && element.key !== EditDate && element.key !== OperationId) {
              logColumns.push({
                title: element.key,
                dataIndex: element.key,
                key: element.key,
                width:
                  element.key === HAStoreName
                    ? HAStoreNameAvailabilitiesLogColLength
                    : element.key === DateHeader
                    ? DateHeaderColLength
                    : element.key === Causal
                    ? CausalColLength
                    : element.key === DocumentationNumber
                    ? DocumentationNumberColLength
                    : MovementAvailabilitiesDefaultColLength,
                render: (text, record, index) => (
                  <div className={element.key === HAStoreName ? 'store-label ' : ''}>{text ? text : ''}</div>
                ),
                className: '',
              })
            }
          }

          if (isDetailsExpand) {
            if (element.key === UserDetail || element.key === EditDate || element.key === OperationId) {
              logColumns.push({
                title: element.key,
                dataIndex: element.key,
                key: element.key,
                width:
                  element.key === EditDate
                    ? EditDateColLength
                    : element.key === OperationId
                    ? OperationIdColLength
                    : MovementAvailabilitiesDefaultColLength,
                render: (text, record, index) => (
                  <div>
                    {(element.key === EditDate || element.key === UserDetail
                      ? renderTranslation(
                          language,
                          TranslationKeys[AvailTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                        ) + ': '
                      : '') +
                      (element.key === EditDate ? dayjs(text).format(DateFormat + ' ' + TimeFormat) : text ? text : '')}
                  </div>
                ),
                className: '',
              })
            }
          }
        })
      }

      const width =
        logColumns.reduce((prev, curr) => {
          return prev + getColumnWidth(curr)
        }, 0) + HandlingExpandColLength

      return (
        <Table
          rowKey={record => record.rowId}
          id='availabilitiesLogTable'
          tableLayout='fixed'
          columns={logColumns}
          dataSource={logData}
          rowClassName={(record, index) => {
            if (record.deleted) {
              return 'deleted'
            }

            if (record.differences && record.differences.length !== 0) {
              return 'fields-changes'
            }
          }}
          pagination={false}
          showHeader={false}
          style={{ width: width }}
          size='small'
        />
      )
    },
    [updateLogTableColumns, isDetailsExpand, language, getColumnWidth],
  )

  const rowExpandable = useCallback(record => {
    return record.logs && record.logs.length !== 0 ? true : false
  }, [])

  const getFirstColumWidthIfIsSet = useCallback(() => {
    if (isEnableMovementLogActive) {
      const tableElement = document.getElementById('availabilitiesTable')
      if (tableElement) {
        const expandRowElement = tableElement.getElementsByClassName('ant-table-row-expand-icon-cell')

        return expandRowElement && expandRowElement.length !== 0
          ? (expandRowElement[0] as HTMLElement).offsetWidth
          : HandlingExpandColLength
      }

      return HandlingExpandColLength
    }

    return 0
  }, [isEnableMovementLogActive])

  const width = useMemo(() => {
    return (
      renderedColumns.reduce((prev, curr) => {
        return prev + getColumnWidth(curr)
      }, 0) + getFirstColumWidthIfIsSet()
    )
  }, [getColumnWidth, getFirstColumWidthIfIsSet, renderedColumns])

  const paginationInfo = useMemo(() => {
    return {
      position: ['bottomRight'],
      total: sortedData.length,
      showSizeChanger: true,
    } as TablePaginationConfig
  }, [sortedData.length])

  return (
    <Table
      rowKey={record => record.teId}
      id='availabilitiesTable'
      columns={renderedColumns}
      tableLayout='fixed'
      dataSource={sortedData}
      expandable={
        isEnableMovementLogActive
          ? {
              expandedRowRender: expandedLogRowRender,
              rowExpandable: rowExpandable,
              expandIcon: ({ expanded, onExpand, record }) =>
                record.logs && record.logs.length !== 0 ? (
                  expanded ? (
                    <CaretDownOutlined
                      onClick={e => onExpand(record, e)}
                      className='availabilities-caret-down caret-icon-down'
                    />
                  ) : (
                    <CaretRightOutlined
                      onClick={e => onExpand(record, e)}
                      className='availabilities-caret-right caret-icon-right'
                    />
                  )
                ) : null,
              columnWidth: HandlingExpandColLength,
            }
          : null
      }
      rowClassName={(record, index) => {
        if (record.deleted) {
          return 'deleted'
        }

        if (record.added) {
          return 'added'
        }
      }}
      pagination={paginationInfo}
      showSorterTooltip={false}
      style={{ width: width }}
      size='small'
    />
  )
}

export default Availabilities
